import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Typography, Grid, Card, Paper, Button, IconButton, Chip,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Avatar, Divider, Tooltip, useMediaQuery, useTheme, MenuItem,
} from "@mui/material";
import {
  Assignment, CheckCircle, HourglassEmpty, Add, Visibility,
  Edit, Close, Person, CalendarMonth, AssignmentInd,
  AddPhotoAlternate, Delete, CheckCircleOutline,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  getAllDailyTasksAction,
  getAllUserAction,
  updateDailyTaskAction,
  clearUpdateDailyTaskAction,
} from "../../store/actions";

// Roles that can assign tasks
const ASSIGN_ROLES = ["ADMIN", "ZM", "AE", "RM", "AM", "SO"];
const TAB_ASSIGNER_ROLES = ["RM", "AM", "SO"];
const ADMIN_TAB_ROLES = ["ADMIN", "ZM", "AE"];

const STATUS_COLOR = {
  ASSIGNED: "warning",
  IN_PROGRESS: "info",
  PENDING: "warning",
  COMPLETED: "success",
  APPROVED: "primary",
  REJECTED: "error",
  CANCELLED: "default",
};

const STATUS_LABEL = {
  ASSIGNED: "Assigned",
  IN_PROGRESS: "In Progress",
  PENDING: "Pending",
  COMPLETED: "Completed",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
};

const TASK_STATUS_FILTERS = [
  { value: "ALL", label: "All Statuses" },
  { value: "PENDING", label: "Pending" },
  { value: "COMPLETED", label: "Completed" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

function normalizeTaskStatus(status = "") {
  const value = String(status || "").trim().toUpperCase();
  if (value === "CANCELED") return "CANCELLED";
  return value;
}

function getTaskStatusLabel(status) {
  const normalizedStatus = normalizeTaskStatus(status);
  return STATUS_LABEL[normalizedStatus] || status || "—";
}

function getTaskStatusColor(status) {
  const normalizedStatus = normalizeTaskStatus(status);
  return STATUS_COLOR[normalizedStatus] || "default";
}

function getInitials(name = "") {
  return (name || "")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// ---------- Stat Card ----------
function StatCard({ icon, label, value, bg }) {
  return (
    <Card
      sx={{
        p: 2,
        borderRadius: 3,
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "left", mb: 0 }}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={600} sx={{ textAlign: "left", mb: 0 }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            bgcolor: bg,
            width: 44,
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 2,
          }}
        >
          {icon}
        </Box>
      </Box>
    </Card>
  );
}

// ---------- View Details Dialog ----------
function ViewDialog({ task, open, onClose, userMap }) {
  if (!task) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontFamily="DM Sans, sans-serif" noWrap sx={{ maxWidth: "85%" }}>
            {task.taskTitle || "Task Details"}
          </Typography>
          <IconButton size="small" onClick={onClose}><Close /></IconButton>
        </Stack>
        <Chip
          label={getTaskStatusLabel(task.status)}
          size="small"
          color={getTaskStatusColor(task.status)}
          sx={{ mt: 0.5 }}
        />
      </DialogTitle>
      <DialogContent dividers>
        {task.taskDescription && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {task.taskDescription}
          </Typography>
        )}
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" gap={1} alignItems="flex-start">
              <AssignmentInd fontSize="small" sx={{ color: "text.secondary", mt: 0.3 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {userMap[task.assignedToUserId] || task.assignedToUserId || "—"}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" gap={1} alignItems="flex-start">
              <Person fontSize="small" sx={{ color: "text.secondary", mt: 0.3 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Assigned By</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {task.assignedByUserName || "—"}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" gap={1} alignItems="flex-start">
              <CalendarMonth fontSize="small" sx={{ color: "text.secondary", mt: 0.3 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Task Date</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {task.taskDate ? dayjs(task.taskDate).format("DD MMM YYYY") : "—"}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          {task.completionTime && (
            <Grid item xs={12} sm={6}>
              <Stack direction="row" gap={1} alignItems="flex-start">
                <CheckCircleOutline fontSize="small" sx={{ color: "success.main", mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Completion Time</Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {dayjs(task.completionTime).format("DD MMM YYYY, h:mm A")}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          )}
        </Grid>
        {task.completionRemarks && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Completion Remarks</Typography>
            <Box sx={{ bgcolor: "#F4F7FE", borderRadius: 2, p: 1.5 }}>
              <Typography variant="body2" color="text.secondary">{task.completionRemarks}</Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- Complete Task Dialog ----------
function CompleteDialog({ task, open, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [remarks, setRemarks] = useState("");
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);

  const { loading, successData, error, errorInfo } = useSelector(
    (state) => state.updateDailyTaskReducer || {}
  );

  useEffect(() => {
    if (!open) { setRemarks(""); setPhotos([]); setPreviews([]); }
  }, [open]);

  useEffect(() => {
    if (successData && open) {
      toast.success("Task marked as completed!");
      dispatch(clearUpdateDailyTaskAction());
      onSuccess();
    }
  }, [successData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error && open) {
      toast.error(errorInfo || "Failed to update task.");
      dispatch(clearUpdateDailyTaskAction());
    }
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  function addPhotos(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setPhotos((p) => [...p, ...files]);
    setPreviews((p) => [...p, ...files.map((f) => URL.createObjectURL(f))]);
    e.target.value = "";
  }

  function removePhoto(i) {
    URL.revokeObjectURL(previews[i]);
    setPhotos((p) => p.filter((_, idx) => idx !== i));
    setPreviews((p) => p.filter((_, idx) => idx !== i));
  }

  function handleSubmit() {
    if (!remarks.trim()) { toast.error("Please enter completion remarks."); return; }
    const fd = new FormData();
    fd.append("status", "COMPLETED");
    fd.append("remarks", remarks.trim());
    photos.forEach((photo) => fd.append("photos", photo));
    fd.endPoint = `daily-task/${task.taskId}`;
    dispatch(updateDailyTaskAction(fd));
  }

  if (!task) return null;
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontFamily="DM Sans, sans-serif">Mark as Completed</Typography>
          <IconButton size="small" onClick={onClose} disabled={loading}><Close /></IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" mt={0.5} fontFamily="DM Sans, sans-serif">
          {task.taskTitle}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Completion Remarks *"
          fullWidth
          multiline
          rows={3}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Describe what was done…"
          size="small"
          sx={{ mb: 2 }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddPhotoAlternate />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ textTransform: "none", borderColor: "#4318FF", color: "#4318FF", mb: previews.length ? 1.5 : 0 }}
        >
          Add Photos
        </Button>
        <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={addPhotos} />
        {previews.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
            {previews.map((src, i) => (
              <Box key={i} sx={{ position: "relative", width: 72, height: 72 }}>
                <Box
                  component="img" src={src} alt=""
                  sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: 1, border: "1px solid #E8EDF5" }}
                />
                <IconButton
                  size="small" onClick={() => removePhoto(i)}
                  sx={{ position: "absolute", top: -6, right: -6, bgcolor: "error.main", color: "#fff", width: 20, height: 20, "&:hover": { bgcolor: "error.dark" } }}
                >
                  <Delete sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit" disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutline />}
        >
          {loading ? "Submitting…" : "Mark as Completed"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DailyTaskList() {
    // Month filter state: default to current month
    const [monthFilter, setMonthFilter] = useState(dayjs().format('YYYY-MM'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isIpad = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const userDetails = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("userDetails")); }
    catch { return null; }
  }, []);

  const currentUserId = userDetails?.id || "";
  const currentRole   = String(userDetails?.role || "").toUpperCase();
  const canAssign     = ASSIGN_ROLES.includes(currentRole);
  // Only show tabs for non-admin roles
  const showTaskTabs  = TAB_ASSIGNER_ROLES.includes(currentRole);
  const [activeTab, setActiveTab] = useState("YOUR_TASKS");
  const [statusFilter, setStatusFilter] = useState("ALL");

  // Base path for "create" navigation (works from both layouts)
  const basePath = location.pathname.startsWith("/masterData")
    ? "/masterData"
    : location.pathname.startsWith("/amrm")
    ? "/amrm"
    : "/SOorMDO";

  const { successData: tasksData, loading: tasksLoading } = useSelector(
    (state) => state.getAllDailyTasksReducer || {}
  );
  const { successData: usersData } = useSelector((state) => state.getAllUserReducer || {});

  useEffect(() => {
    dispatch(getAllDailyTasksAction());
    dispatch(getAllUserAction());
  }, [dispatch]);

  // Build user map: userId -> name
  const userMap = useMemo(() => {
    const users = Array.isArray(usersData)
      ? usersData
      : Array.isArray(usersData?.data?.users)
      ? usersData.data.users
      : [];
    const map = {};
    users.forEach((u) => { if (u.userId) map[u.userId] = u.name || u.userId; });
    return map;
  }, [usersData]);

  const allTasks = useMemo(() => {
    if (Array.isArray(tasksData?.data?.dailyTasks)) return tasksData.data.dailyTasks;
    if (tasksData?.data && typeof tasksData.data === 'object' && Array.isArray(tasksData.data.dailyTasks)) return tasksData.data.dailyTasks;
    if (Array.isArray(tasksData?.data)) return tasksData.data;
    if (Array.isArray(tasksData)) return tasksData;
    return [];
  }, [tasksData]);

  // Only non-admin roles use tabs and filtered lists
  const [yourTasks, setYourTasks] = useState([]);
  const [yourTasksLoading, setYourTasksLoading] = useState(false);
  const [assignedByYouTasks, setAssignedByYouTasks] = useState([]);
  const [assignedByYouLoading, setAssignedByYouLoading] = useState(false);

  useEffect(() => {
    if (!ADMIN_TAB_ROLES.includes(currentRole)) {
      if (activeTab === "YOUR_TASKS") {
        setYourTasksLoading(true);
        dispatch(getAllDailyTasksAction({ endPoint: `daily-task?assigned_to_user_id=${currentUserId}` }));
      }
      if (activeTab === "ASSIGNED_BY_YOU") {
        setAssignedByYouLoading(true);
        dispatch(getAllDailyTasksAction({ endPoint: `daily-task?assigned_by_user_id=${currentUserId}` }));
      }
    }
  }, [activeTab, currentUserId, dispatch, currentRole]);

  useEffect(() => {
    if (!ADMIN_TAB_ROLES.includes(currentRole)) {
      if (activeTab === "YOUR_TASKS" && tasksData) {
        if (Array.isArray(tasksData?.data?.dailyTasks)) {
          setYourTasks(tasksData.data.dailyTasks);
        } else if (Array.isArray(tasksData?.data)) {
          setYourTasks(tasksData.data);
        } else if (Array.isArray(tasksData)) {
          setYourTasks(tasksData);
        } else {
          setYourTasks([]);
        }
        setYourTasksLoading(false);
      }
      if (activeTab === "ASSIGNED_BY_YOU" && tasksData) {
        if (Array.isArray(tasksData?.data?.dailyTasks)) {
          setAssignedByYouTasks(tasksData.data.dailyTasks);
        } else if (Array.isArray(tasksData?.data)) {
          setAssignedByYouTasks(tasksData.data);
        } else if (Array.isArray(tasksData)) {
          setAssignedByYouTasks(tasksData);
        } else {
          setAssignedByYouTasks([]);
        }
        setAssignedByYouLoading(false);
      }
      if (activeTab !== "YOUR_TASKS") {
        setYourTasks([]);
        setYourTasksLoading(false);
      }
      if (activeTab !== "ASSIGNED_BY_YOU") {
        setAssignedByYouTasks([]);
        setAssignedByYouLoading(false);
      }
    }
  }, [tasksData, activeTab, currentRole]);

  // For ADMIN, AE, ZM always show all tasks for current month, no tabs
  let baseTasks;
  if (ADMIN_TAB_ROLES.includes(currentRole)) {
    baseTasks = allTasks;
  } else if (activeTab === "YOUR_TASKS" && TAB_ASSIGNER_ROLES.includes(currentRole)) {
    baseTasks = yourTasks;
  } else if (activeTab === "ASSIGNED_BY_YOU") {
    baseTasks = assignedByYouTasks;
  } else {
    baseTasks = allTasks;
  }

  // Filter by month (always for ADMIN, AE, ZM)
  const tasksByMonth = baseTasks.filter((t) => {
    if (!t.taskDate) return false;
    const taskMonth = dayjs(t.taskDate).format('YYYY-MM');
    return taskMonth === monthFilter;
  });

  // Filter by status
  const tasks = statusFilter !== "ALL"
    ? tasksByMonth.filter((t) => {
        const taskStatus = normalizeTaskStatus(t.status);
        if (statusFilter === "PENDING") {
          return ["ASSIGNED", "IN_PROGRESS", "PENDING"].includes(taskStatus);
        }
        return taskStatus === statusFilter;
      })
    : tasksByMonth;

  // Desktop grid columns
  const gridCols = canAssign
    ? "minmax(140px, 2fr) 1.4fr 1.4fr 1fr 1fr 1.3fr 72px"
    : "minmax(160px, 2fr) 1.4fr 1fr 1fr 1.3fr 72px";

  const desktopHeaders = canAssign
    ? ["Title", "Assigned By", "Assigned To", "Task Date", "Status", "Completion Time", "Actions"]
    : ["Title", "Assigned By", "Task Date", "Status", "Completion Time", "Actions"];

  const total     = tasks.length;
  const pending = tasks.filter((t) => ["ASSIGNED", "IN_PROGRESS", "PENDING"].includes(normalizeTaskStatus(t.status))).length;
  const completed = tasks.filter((t) => normalizeTaskStatus(t.status) === "COMPLETED").length;

  const stats = [
    { label: "Total Tasks",  value: total,     bg: "#E8F0FF", icon: <Assignment  sx={{ color: "#2563EB", fontSize: 22 }} /> },
    { label: "Pending",      value: pending,   bg: "#FFF3E0", icon: <HourglassEmpty sx={{ color: "#F59E42", fontSize: 22 }} /> },
    { label: "Completed",    value: completed, bg: "#E8FFF1", icon: <CheckCircle  sx={{ color: "#059669", fontSize: 22 }} /> },
  ];

  const [viewTask, setViewTask]       = useState(null);
  const [completeTask, setCompleteTask] = useState(null);

  function refreshTasks() {
    dispatch(getAllDailyTasksAction());
    setCompleteTask(null);
  }

  return (
    <Box minHeight="100vh" p={2}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "#1B2559", fontFamily: "DM Sans, sans-serif" }}>
            Daily Tasks
          </Typography>
        </Box>
      </Stack>

      {/* Stat Cards */}
      <Box display="flex" gap={2} sx={{ mb: canAssign ? 2 : 3 }}>
        {stats.map((s) => (
          <StatCard
            key={s.label}
            label={s.label}
            value={s.value}
            bg={s.bg}
            icon={s.icon}
          />
        ))}
      </Box>

      {/* Task Buttons + Assign Task Button */}
      {canAssign && (
        <Card sx={{ borderRadius: 3, p: 2, mb: 3 }}>
          <Grid container spacing={1.5} alignItems="center">
            {/* Only show tabs column for non-admin roles */}
            {!ADMIN_TAB_ROLES.includes(currentRole) && (
              <Grid item size={{xs:12,sm:6,md:3}}>
                {showTaskTabs ? (
                  <Stack direction={{ xs: "column", md: "row" }} gap={1}>
                    <Button
                      variant={activeTab === "YOUR_TASKS" ? "contained" : "outlined"}
                      onClick={() => setActiveTab("YOUR_TASKS")}
                      sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 600,
                      }}
                    >
                      Your Tasks
                    </Button>
                    <Button
                      variant={activeTab === "ASSIGNED_BY_YOU" ? "contained" : "outlined"}
                      onClick={() => setActiveTab("ASSIGNED_BY_YOU")}
                      sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 600,
                        minWidth: 140,
                      }}
                    >
                      Assigned By You
                    </Button>
                  </Stack>
                ) : null}
              </Grid>
            )}
            <Grid item size={{xs:12,sm:6,md:ADMIN_TAB_ROLES.includes(currentRole)?4:3}}>
              <TextField
                select
                size="small"
                label="Status Filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              >
                {TASK_STATUS_FILTERS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item size={{xs:12,sm:6,md:ADMIN_TAB_ROLES.includes(currentRole)?4:3}}>
              <TextField
                select
                size="small"
                label="Month Filter"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              >
                {/* Generate last 12 months for dropdown */}
                {Array.from({ length: 12 }).map((_, idx) => {
                  const m = dayjs().subtract(idx, 'month');
                  return (
                    <MenuItem key={m.format('YYYY-MM')} value={m.format('YYYY-MM')}>
                      {m.format('MMMM YYYY')}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid item size={{xs:12,sm:6,md:ADMIN_TAB_ROLES.includes(currentRole)?4:3}}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate(`${basePath}/dailyTaskcreate`)}
                sx={{
                  fontFamily: "DM Sans, sans-serif",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "10px",
                  py: 1,
                  background: "linear-gradient(106.11deg, #4F46E5 0%, #3A349B 100%)",
                  boxShadow: "0px 4px 16px rgba(67,24,255,0.2)",
                  "&:hover": { background: "linear-gradient(106.11deg, #3A349B 0%, #2D2880 100%)" },
                }}
              >
                Assign Task
              </Button>
            </Grid>
          </Grid>
        </Card>
      )}

      {/* Table */}
      <Grid>
        {tasksLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress sx={{ color: "#4318FF" }} />
          </Box>
        ) : tasks.length === 0 ? (
          <Box textAlign="center" py={6}>
            <Assignment sx={{ fontSize: 52, color: "#A3AED0", mb: 1 }} />
            <Typography sx={{ color: "#A3AED0", fontFamily: "DM Sans, sans-serif" }}>No tasks found</Typography>
          </Box>
        ) : (
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            {isMobile ? (
              /* ---- MOBILE CARDS ---- */
              <Grid container spacing={2}>
                {tasks.map((task) => (
                  <Grid item size={{xs:12}} key={task.taskId}>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        p: 1.5,
                        border: "1px solid #E5E7EB",
                        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                        "&:hover, &:active": {
                          borderColor: "#4318FF",
                          boxShadow: "0 4px 14px rgba(67,24,255,0.18)",
                        },
                      }}
                    >
                      {/* Title + Status */}
                      <Box mb={1} display="flex" alignItems="flex-start" gap={1}>
                        <Typography fontWeight={700} variant="body2" sx={{ color: "#1B2559", flex: 1 }}>
                          {task.taskTitle || "—"}
                        </Typography>
                        <Chip
                          label={getTaskStatusLabel(task.status)}
                          size="small"
                          color={getTaskStatusColor(task.status)}
                          sx={{ height: 22, flexShrink: 0, "& .MuiChip-label": { fontSize: 11, fontWeight: 600 } }}
                        />
                      </Box>
                     

                      <Box mb={0.75} display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" fontWeight={600} minWidth={110} sx={{ color: "#64748B" }}>Assigned By:</Typography>
                        <Typography variant="caption">{task.assignedByUserName || "—"}</Typography>
                      </Box>

                      <Box mb={0.75} display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" fontWeight={600} minWidth={110} sx={{ color: "#64748B" }}>Assigned To:</Typography>
                        <Typography variant="caption">{userMap[task.assignedToUserId] || task.assignedToUserId || "—"}</Typography>
                      </Box>

                      <Box mb={0.75} display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" fontWeight={600} minWidth={110} sx={{ color: "#64748B" }}>Task Date:</Typography>
                        <Typography variant="caption">{task.taskDate ? dayjs(task.taskDate).format("DD MMM YYYY") : "—"}</Typography>
                      </Box>

                      <Box mb={0.75} display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" fontWeight={600} minWidth={110} sx={{ color: "#64748B" }}>Completion Time:</Typography>
                        <Typography variant="caption" sx={{ color: task.completionTime ? "success.main" : "#A3AED0" }}>
                          {task.completionTime ? dayjs(task.completionTime).format("DD MMM YYYY, h:mm A") : "—"}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mt={1}>
                        {(canAssign || task.assignedToUserId === currentUserId) && (
                          <Tooltip title="Edit Task">
                            <IconButton size="small" onClick={() => navigate(`${basePath}/dailyTaskedit`, { state: { taskId: task.taskId } })} sx={{ color: "#4318FF" }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => setViewTask(task)} sx={{ color: "#64748B" }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
               
            ) : (
              /* ---- DESKTOP GRID ---- */
              <Box display="flex" flexDirection="column" gap={1}>
                {/* Header row */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: gridCols,
                    alignItems: "center",
                    px: 1.5,
                    py: 1,
                    gap: 1,
                  }}
                >
                  {desktopHeaders.map((h) => (
                    <Typography
                      key={h}
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        textAlign: h === "Actions" ? "right" : "left",
                      }}
                    >
                      {h}
                    </Typography>
                  ))}
                </Box>

                {/* Data rows */}
                {tasks.map((task) => (
                  <Paper
                    key={task.taskId}
                    elevation={0}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: gridCols,
                      alignItems: "center",
                      border: "1px solid #E5E7EB",
                      borderRadius: 3,
                      px: 2,
                      py: 1.5,
                      gap: 1,
                      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        borderColor: "#4318FF",
                        boxShadow: "0 4px 14px rgba(67,24,255,0.18)",
                      },
                    }}
                  >
                    {/* Title */}
                    <Box overflow="hidden">
                      <Typography variant="body2" fontWeight={600} sx={{ color: "#1B2559" }} noWrap>
                        {task.taskTitle || "—"}
                      </Typography>
                    </Box>

                    {/* Assigned By */}
                    <Typography variant="body2" sx={{ color: "#1B2559" }} noWrap>
                      {task.assignedByUserName || "—"}
                    </Typography>

                    {/* Assigned To (canAssign only) */}
                    {canAssign && (
                      <Typography variant="body2" noWrap sx={{ color: "#1B2559" }}>
                        {userMap[task.assignedToUserId] || task.assignedToUserId || "—"}
                      </Typography>
                    )}

                    {/* Task Date */}
                    <Typography variant="body2" noWrap>
                      {task.taskDate ? dayjs(task.taskDate).format("DD MMM YYYY") : "—"}
                    </Typography>

                    {/* Status */}
                    <Chip
                      label={getTaskStatusLabel(task.status)}
                      size="small"
                      color={getTaskStatusColor(task.status)}
                      sx={{ width: "fit-content", "& .MuiChip-label": { fontSize: 12, fontWeight: 600 } }}
                    />

                    {/* Completion Time */}
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: task.completionTime ? "success.main" : "#A3AED0",
                        fontWeight: task.completionTime ? 600 : 400,
                      }}
                    >
                      {task.completionTime
                        ? dayjs(task.completionTime).format("DD MMM YYYY, h:mm A")
                        : "—"}
                    </Typography>

                    {/* Actions */}
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                      {(canAssign || task.assignedToUserId === currentUserId) && (
                        <Tooltip title="Edit Task">
                          <IconButton size="small" onClick={() => navigate(`${basePath}/dailyTaskedit`, { state: { taskId: task.taskId } })} sx={{ color: "#4318FF" }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => setViewTask(task)} sx={{ color: "#64748B" }}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Grid>

      {/* Dialogs */}
      <ViewDialog
        task={viewTask}
        open={!!viewTask}
        onClose={() => setViewTask(null)}
        userMap={userMap}
      />
      <CompleteDialog
        task={completeTask}
        open={!!completeTask}
        onClose={() => setCompleteTask(null)}
        onSuccess={refreshTasks}
      />
    </Box>
  );
}
